import { FC, useEffect, useRef } from 'react';
import {
  AssistantAttachment,
  AssistantMessage as AssistantMessageType,
  AssistantStreamStatus,
  CompanyOption,
  ElicitationAnswer,
  MyGeneralQuota,
  PendingElicitation,
} from '@/types/assistant';
import AssistantMessage from './AssistantMessage';
import AssistantInput from './AssistantInput';
import AssistantThinking from './AssistantThinking';
import ModelBadge from './ModelBadge';
import { ModelIdentity } from '@/utils/modelIdentity';
import CompanyIndicator from './CompanyIndicator';
import ConfidentialityPanel from './ConfidentialityPanel';
import DrlExportButton from './DrlExportButton';
import ReindexButton from './ReindexButton';
import ReviewExportButton from './ReviewExportButton';
import ElicitationCard from './ElicitationCard';

interface Props {
  greetingName: string;
  messages: AssistantMessageType[];
  sending: boolean;
  thinking: boolean;
  activeTool: string | null;
  error: string | null;
  quota: MyGeneralQuota | null;
  /** Modelo do assistente ativo (`agent.model`) — o que VAI responder. */
  assistantModel?: string;
  /** Identidade resolvida do modelo do assistente (`model_info`), do servidor. */
  assistantModelInfo?: ModelIdentity | null;
  onSend: (content: string) => void;
  onStop?: () => void;
  attachments: AssistantAttachment[];
  uploading: boolean;
  onAttachFiles: (files: File[]) => void;
  onRemoveAttachment: (id: string) => void;
  onOpenConversations: () => void;
  onOpenMemory: () => void;
  activeCompany: string | null;
  companies: CompanyOption[];
  onSelectCompany: (companyId: string) => void;
  // J07: identidade do assistente ativo (H.Bot, H.Lex…) + status/confidencialidade.
  assistantName?: string;
  assistantSubtitle?: string;
  assistantAvatar?: string;
  suggestions?: string[];
  legalSteps?: AssistantStreamStatus[];
  confidential?: boolean;
  contracts?: AssistantAttachment[];
  onDeleteContract?: (id: string) => void;
  deletingContractId?: string | null;
  // S06: bot SharePoint (H.Find) — barra de ações (re-indexar / exportar DRL).
  sharepoint?: boolean;
  onReindex?: () => void;
  onExportDrl?: () => void;
  // D04: bot de apresentações (H.Slide) — walkthrough do deck + seletor de empresa.
  deck?: boolean;
  // Q06: bot de revisão de documentos (H.Rev) — walkthrough da revisão + exportar devolutiva.
  quality?: boolean;
  onExportReview?: () => void;
  // E02: elicitação — pergunta pendente do bot (card) + bloqueio do input.
  pendingElicitation?: PendingElicitation | null;
  onElicitationSubmit?: (answers: ElicitationAnswer[]) => void;
}

const DEFAULT_SUGGESTIONS = [
  'Resuma este texto em 3 tópicos',
  'Escreva um e-mail profissional pedindo um prazo',
  'Crie uma planilha de controle de gastos do mês',
  'Me ajude a montar um plano de estudos de IA',
];

/** Avatar do header: nome de Material Symbol (ascii minúsculo) → ícone; senão → emoji/texto. */
function isMaterialSymbol(v: string): boolean {
  return /^[a-z][a-z0-9_]*$/.test(v);
}

const AssistantChat: FC<Props> = ({
  greetingName,
  messages,
  sending,
  thinking,
  activeTool,
  error,
  quota,
  assistantModel,
  assistantModelInfo,
  onSend,
  onStop,
  attachments,
  uploading,
  onAttachFiles,
  onRemoveAttachment,
  onOpenConversations,
  onOpenMemory,
  activeCompany,
  companies,
  onSelectCompany,
  assistantName = 'H.Bot',
  assistantSubtitle = 'Seu assistente de IA do h.lab',
  assistantAvatar = 'auto_awesome',
  suggestions = DEFAULT_SUGGESTIONS,
  legalSteps,
  confidential = false,
  contracts = [],
  onDeleteContract,
  deletingContractId,
  sharepoint = false,
  onReindex,
  onExportDrl,
  deck = false,
  quality = false,
  onExportReview,
  pendingElicitation = null,
  onElicitationSubmit,
}) => {
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const lastLen = messages[messages.length - 1]?.content.length ?? 0;
  // S06: o "Exportar DRL anotado" só faz sentido depois de uma conferência (há drl_result).
  const hasDrlResult = messages.some(
    (m) => m.role === 'ASSISTANT' && Array.isArray(m.metadata?.drl_result?.itens),
  );
  // Q06: "Exportar devolutiva" só faz sentido depois de uma revisão (há metadata.review).
  const hasReview = messages.some(
    (m) => m.role === 'ASSISTANT' && Array.isArray(m.metadata?.review?.findings),
  );

  useEffect(() => {
    if (typeof messagesEndRef.current?.scrollIntoView === 'function') {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages.length, sending, lastLen, pendingElicitation]);

  return (
    <section className="flex-1 flex flex-col min-w-0">
      <header className="border-b border-surface-border bg-surface-primary px-4 py-2 flex items-center gap-2.5">
        <button
          type="button"
          onClick={onOpenConversations}
          className="lg:hidden p-2 hover:bg-surface-hover rounded-lg shrink-0"
          aria-label="Abrir conversas"
        >
          <span className="material-symbols-outlined">forum</span>
        </button>
        <div className="w-8 h-8 rounded-full bg-accent/15 border border-accent/30 grid place-items-center text-accent shrink-0 text-[18px]">
          {isMaterialSymbol(assistantAvatar) ? (
            <span className="material-symbols-outlined text-[20px] leading-none">{assistantAvatar}</span>
          ) : (
            <span aria-hidden>{assistantAvatar}</span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <h1
            className="text-sm font-semibold text-content-primary leading-tight"
            data-testid="assistant-name"
          >
            {assistantName}
          </h1>
          <p className="text-2xs text-content-tertiary truncate leading-tight">{assistantSubtitle}</p>
        </div>
        <ModelBadge
          info={assistantModelInfo}
          model={assistantModel}
          catalog={quota?.available_models_catalog}
        />
        <button
          type="button"
          onClick={onOpenMemory}
          data-testid="open-right-panel"
          className="p-2 hover:bg-surface-hover rounded-lg shrink-0 text-content-tertiary hover:text-content-primary transition"
          aria-label="Abrir memória e cota"
          title="Memória e cota"
        >
          <span className="material-symbols-outlined">neurology</span>
        </button>
      </header>

      {/* G14: seletor de empresa pra geração branded — H.Bot e H.Slide (deck). No jurídico/
          SharePoint (confidential, sem geração de marca) é ruído e fica escondido. */}
      {(!confidential || deck) && (
        <CompanyIndicator
          value={activeCompany}
          companies={companies}
          onSelect={onSelectCompany}
          disabled={sending}
        />
      )}

      {confidential && onDeleteContract && (
        <ConfidentialityPanel
          contracts={contracts}
          onDelete={onDeleteContract}
          deletingId={deletingContractId}
        />
      )}

      {/* S06: ações do H.Find — re-indexar a pasta e exportar o DRL anotado. */}
      {sharepoint && onReindex && onExportDrl && (
        <div
          data-testid="sharepoint-actions"
          className="border-b border-surface-border bg-surface-primary px-4 py-1.5 flex items-center gap-2 flex-wrap"
        >
          <DrlExportButton onExport={onExportDrl} disabled={sending || !hasDrlResult} />
          <ReindexButton onReindex={onReindex} disabled={sending} />
        </div>
      )}

      {/* Q06: ação do H.Rev — exportar a devolutiva (.docx) da revisão. */}
      {quality && onExportReview && (
        <div
          data-testid="quality-actions"
          className="border-b border-surface-border bg-surface-primary px-4 py-1.5 flex items-center gap-2 flex-wrap"
        >
          <ReviewExportButton onExport={onExportReview} disabled={sending || !hasReview} />
        </div>
      )}

      <div
        className="flex-1 min-h-0 overflow-y-auto p-4 md:p-6 space-y-4 bg-surface-primary"
        data-testid="assistant-messages"
      >
        {messages.length === 0 && (
          <div className="max-w-2xl mx-auto py-12">
            <div className="text-center mb-6">
              <span className="material-symbols-outlined text-5xl text-accent mb-3 block">
                auto_awesome
              </span>
              <h2 className="text-2xl font-bold text-content-primary mb-2">
                Oi, {greetingName} 👋
              </h2>
              <p className="text-sm text-content-tertiary">{assistantSubtitle}</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              {suggestions.map((s) => (
                <button
                  key={s}
                  type="button"
                  data-testid="suggestion"
                  onClick={() => onSend(s)}
                  disabled={sending}
                  className="text-left text-sm px-4 py-3 rounded-xl bg-surface-secondary hover:bg-surface-hover border border-surface-border transition-colors disabled:opacity-50"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m) => (
          <AssistantMessage key={m.id} message={m} modelCatalog={quota?.available_models_catalog} />
        ))}

        {/* E02: o bot perguntou (ask_user) → card de resposta (bloqueia o input). */}
        {pendingElicitation && onElicitationSubmit && (
          <ElicitationCard
            questions={pendingElicitation.questions}
            onSubmit={onElicitationSubmit}
            submitting={sending}
          />
        )}

        {thinking && (
          <AssistantThinking
            activeTool={activeTool}
            legalSteps={legalSteps}
            legal={confidential && !sharepoint && !deck && !quality}
            sharepoint={sharepoint}
            deck={deck}
            quality={quality}
          />
        )}

        {error && (
          <div
            data-testid="assistant-error"
            className="mx-auto max-w-2xl text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2"
          >
            {error}
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {sending && onStop && (
        <div className="px-4 pt-2 bg-surface-primary flex justify-center">
          <button
            type="button"
            data-testid="stop-generation"
            onClick={onStop}
            className="text-xs px-3 py-1.5 rounded-full border border-surface-border text-content-tertiary hover:text-content-primary hover:border-accent transition inline-flex items-center gap-1"
          >
            <span className="material-symbols-outlined text-sm leading-none">stop_circle</span>
            Parar geração
          </button>
        </div>
      )}

      <AssistantInput
        disabled={sending || !!pendingElicitation}
        uploading={uploading}
        onSend={onSend}
        attachments={attachments}
        onAttachFiles={onAttachFiles}
        onRemoveAttachment={onRemoveAttachment}
        accept={
          sharepoint
            ? '.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            : deck
              ? '.txt,.md,.pdf,.docx,text/plain,text/markdown,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document'
              : quality
                ? '.pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                : undefined
        }
        placeholder={
          sharepoint
            ? 'Anexe o DRL (.xlsx) e peça pra conferir na pasta…'
            : deck
              ? 'Descreva a apresentação ou anexe um texto/PDF…'
              : quality
                ? 'Anexe o POP ou anexo (PDF) pra eu revisar…'
                : undefined
        }
      />
    </section>
  );
};

export default AssistantChat;
